









import Vue from 'vue'

import types from '../../store/types'
import {Shape, DragParameter} from '../../interface'

import EditorImageMask from './ImageMask.vue'
import EditorImageLine from './ImageLine.vue'

export default Vue.extend({
  components: {
    EditorImageMask,
    EditorImageLine
  },

  data(): {
    shapeInfo: Shape | null,
    caller: any,
    progressCaller: any,
    finishCaller: any,
    imageWrap: HTMLElement | null,
    availableTouch: boolean,
    dragParameter: DragParameter
  } {
    return {
      shapeInfo: null,
      caller: null,
      progressCaller: null,
      finishCaller: null,
      imageWrap: null,
      availableTouch: window.ontouchstart === null,
      dragParameter: {
        previousX: 0,
        previousY: 0,
        originalScale: 0,
        originalRotate: 0,
        previousDistance: null,
        previousRadian: null,
        isMultiTouch: false
      }
    }
  },

  computed: {
    image(): string {
      return this.$store.state.Current.file.result
    }
  },

  beforeMount() {
    const shapeIndex: number = this.$store.state.Current.shape
    const shapeList: Shape[] = this.$store.state.Shape.list
    this.shapeInfo = shapeList[shapeIndex]
  },

  mounted() {
    this.imageWrap = document.getElementById('image-wrap')

    this.caller = this.resize.bind(this)
    window.addEventListener('resize', this.caller)
  },

  destroyed() {
    window.removeEventListener('resize', this.caller)
  },

  methods: {
    resize() {
      if (! this.shapeInfo || ! this.imageWrap) {
        return {}
      }

      const scale: number = this.imageWrap.offsetWidth / this.shapeInfo.width
      this.$store.commit(types.mutations.SHAPE_SCALE, scale)
    },

    /**
     * Mouse Function
     */
    mouseDownHandler(e: MouseEvent) {
      if (this.availableTouch) {
        return
      }
      e.preventDefault()

      this.dragStartHandler(e)

      this.progressCaller = this.mouseMoveHandler.bind(this)
      this.finishCaller = this.mouseUpHandler.bind(this)

      window.addEventListener('mousemove', this.progressCaller)
      window.addEventListener('mouseup', this.finishCaller)
    },

    mouseMoveHandler(e: MouseEvent) {
      const deltaX = e.pageX - this.dragParameter.previousX
      const deltaY = e.pageY - this.dragParameter.previousY

      this.setCoordinates(deltaX, deltaY)

      this.dragParameter.previousX = e.pageX
      this.dragParameter.previousY = e.pageY
    },

    mouseUpHandler(e: MouseEvent) {
      window.removeEventListener('mousemove', this.progressCaller)
      window.removeEventListener('mouseup', this.finishCaller)
    },

    /**
     * Touch Function
     */
    touchStartHandler(e: TouchEvent) {
      if (! this.availableTouch) {
        return
      }
      e.preventDefault()

      this.dragStartHandler(e.touches[0])

      if (this.progressCaller === null) {
        this.progressCaller = this.touchMoveHandler.bind(this)
        this.finishCaller = this.touchEndHandler.bind(this)

        window.addEventListener('touchmove', this.progressCaller)
        window.addEventListener('touchend', this.finishCaller)
      }
    },

    touchMoveHandler(e: TouchEvent) {
      if (e.touches.length === 1) {
        this.dragParameter.isMultiTouch = false
      } else {
        this.dragParameter.isMultiTouch = true

        this.changeScaleRotate(e)
      }

      const touch: Touch = e.touches[0]

      const deltaX = touch.pageX - this.dragParameter.previousX
      const deltaY = touch.pageY - this.dragParameter.previousY

      if (! this.dragParameter.isMultiTouch) {
        this.setCoordinates(deltaX, deltaY)
      }

      this.dragParameter.previousX = touch.pageX
      this.dragParameter.previousY = touch.pageY
    },

    touchEndHandler(e: TouchEvent) {
      if (e.touches.length < 1) {
        window.removeEventListener('touchmove', this.progressCaller)
        window.removeEventListener('touchend', this.finishCaller)

        this.progressCaller = null
        this.finishCaller = null
      } else {
        this.dragStartHandler(e.touches[0])
      }
    },

    changeScaleRotate(e: TouchEvent) {
        const touch1 = e.touches[0]
        const touch2 = e.touches[1]

        const dist = this.calcDistance(touch1, touch2)
        if (this.dragParameter.previousDistance === null) {
            this.dragParameter.previousDistance = dist
        }
        const scale: number =
          this.dragParameter.originalScale *
          (dist / this.dragParameter.previousDistance)
        this.$store.commit(types.mutations.CURRENT_SCALE, scale)

        const radians = this.calcRadian(touch1, touch2)
        if (this.dragParameter.previousRadian === null) {
            this.dragParameter.previousRadian = radians
        }
        const rotate =
          this.dragParameter.originalRotate +
          ((radians - this.dragParameter.previousRadian) * 180 / Math.PI)
        this.$store.commit(types.mutations.CURRENT_ROTATE, rotate)
    },

    /**
     * Calculate distance
     *
     * @access private
     * @param touch1
     * @param touch2
     * @return number
     */
    calcDistance(touch1: any, touch2: any): number {
        return Math.sqrt(
            (touch1.pageX - touch2.pageX) *
            (touch1.pageX - touch2.pageX) +
            (touch1.pageY - touch2.pageY) *
            (touch1.pageY - touch2.pageY)
        )
    },

    /**
     * Calculate Radian
     *
     * @access private
     * @param touch1
     * @param touch2
     * @return number
     * via: https://stackoverflow.com/questions/13863974/rotate-element-based-on-touch-event
     */
    calcRadian(touch1: any, touch2: any): number {
        return Math.atan2(
            (touch1.pageY - touch2.pageY),
            (touch1.pageX - touch2.pageX)
        )
    },

    /**
     * Common Function
     */
    dragStartHandler(e: Touch | MouseEvent) {
      this.dragParameter.previousX = e.pageX
      this.dragParameter.previousY = e.pageY
      this.dragParameter.originalScale = this.$store.state.Current.scale
      this.dragParameter.originalRotate = this.$store.state.Current.rotate
      this.dragParameter.previousDistance = null
      this.dragParameter.previousRadian = null
      this.dragParameter.isMultiTouch = false
    },

    setCoordinates(deltaX: number, deltaY: number) {
      if (! this.imageWrap) {
        return
      }

      const width: number = this.$store.state.Current.image_width
      const height: number = this.$store.state.Current.image_height
      const widthRatio: number = width / height

      const size: number = this.imageWrap.offsetWidth
      const x: number = this.$store.state.Current.x + deltaX / size * 100
      const y: number = this.$store.state.Current.y + deltaY / size * 100 * widthRatio

      this.$store.commit(types.mutations.CURRENT_X, x)
      this.$store.commit(types.mutations.CURRENT_Y, y)
    }
  }
})
